import { Fragment, useEffect, useMemo, useState } from "react";

import AccessTimeRoundedIcon from "@mui/icons-material/AccessTimeRounded";
import BlockRoundedIcon from "@mui/icons-material/BlockRounded";
import CalendarMonthRoundedIcon from "@mui/icons-material/CalendarMonthRounded";
import CampaignRoundedIcon from "@mui/icons-material/CampaignRounded";
import CheckCircleOutlineRoundedIcon from "@mui/icons-material/CheckCircleOutlineRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import DoneAllRoundedIcon from "@mui/icons-material/DoneAllRounded";
import EmailRoundedIcon from "@mui/icons-material/EmailRounded";
import ErrorOutlineRoundedIcon from "@mui/icons-material/ErrorOutlineRounded";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import KeyboardArrowUpRoundedIcon from "@mui/icons-material/KeyboardArrowUpRounded";
import KeyboardArrowDownRoundedIcon from "@mui/icons-material/KeyboardArrowDownRounded";
import OpenInNewRoundedIcon from "@mui/icons-material/OpenInNewRounded";
import SmsRoundedIcon from "@mui/icons-material/SmsRounded";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import {
  Avatar,
  Box,
  Button,
  CircularProgress,
  Dialog,
  IconButton,
  Menu,
  MenuItem,
  Stack,
  Tooltip,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";

import DeliveredMessagePreview, {
  getDeliveredPreviewText,
} from "../DeliveredMessagePreview";
import { BadgeBase } from "../../ui";
import TimelineContactCard, {
  getMessageContactCard,
  hasMessageContactCard,
} from "./components/TimelineContactCard";
import MessageHistoryFilters from "./components/MessageHistoryFilters";
import MessageMetadataMenu from "./components/MessageMetadataMenu";
import TimelineMediaAttachment, {
  formatFileSize,
  getMediaPresentation,
  getMediaStatusPresentation,
  getMessageMediaAsset,
  hasMessageMedia,
} from "./components/TimelineMediaAttachment";
import { getTimelineGroupId } from "../../../lib/conversationHistory/timelineGroups";

const dateFormatter = new Intl.DateTimeFormat("pt-BR", {
  day: "2-digit",
  hour: "2-digit",
  minute: "2-digit",
  month: "2-digit",
  year: "2-digit",
});

const dayFormatter = new Intl.DateTimeFormat("pt-BR", {
  day: "2-digit",
  month: "long",
  year: "numeric",
});

const shortDayFormatter = new Intl.DateTimeFormat("pt-BR", {
  day: "2-digit",
  month: "2-digit",
  year: "2-digit",
});

const timeFormatter = new Intl.DateTimeFormat("pt-BR", {
  hour: "2-digit",
  minute: "2-digit",
});

const channelConfig = {
  email: { icon: EmailRoundedIcon, label: "E-mail", tone: "blue" },
  sms: { icon: SmsRoundedIcon, label: "SMS", tone: "warning" },
  whatsapp_api: { icon: WhatsAppIcon, label: "WhatsApp", tone: "success" },
};

function getMessageBubbleStyle(theme, direction) {
  const isDark = theme.palette.mode === "dark";
  if (direction === "user") {
    return {
      backgroundColor: isDark
        ? alpha(theme.palette.common.white, 0.06)
        : "#f1f5f9",
      borderColor: isDark
        ? alpha(theme.palette.common.white, 0.1)
        : "#e2e8f0",
    };
  }

  return {
    backgroundColor: isDark
      ? alpha(theme.palette.custom.blue[900], 0.22)
      : theme.palette.custom.blue[50],
    borderColor: isDark
      ? alpha(theme.palette.custom.blue[200], 0.28)
      : alpha(theme.palette.custom.blue[200], 0.7),
  };
}

const createPreviewSurfaceSx = (direction) => (theme) => {
  const bubbleStyle = getMessageBubbleStyle(theme, direction);
  return {
    "& .DeliveredMessagePreview-surface": {
      backgroundColor: bubbleStyle.backgroundColor,
      borderColor: bubbleStyle.borderColor,
      minHeight: "auto",
    },
  };
};

const companyPreviewSurfaceSx = createPreviewSurfaceSx("company");
const userPreviewSurfaceSx = createPreviewSurfaceSx("user");
const unavailablePreviewSurfaceSx = createPreviewSurfaceSx("company");

const timelineMessageMaxWidthSx = {
  xs: "100%",
  sm: "min(84%, 640px)",
  md: "min(68%, 720px)",
  lg: "min(64%, 760px)",
};

const timelineMessageFrameSx = {
  maxWidth: timelineMessageMaxWidthSx,
  minWidth: { xs: "min(100%, 180px)", sm: 220 },
  position: "relative",
  width: { xs: "100%", sm: "fit-content" },
};

const chatScrollAreaSx = (theme) => ({
  overflowX: "hidden",
  overflowY: "auto",
  scrollbarColor: `${alpha(theme.palette.text.secondary, 0.45)} transparent`,
  scrollbarWidth: "thin",
  "&::-webkit-scrollbar": {
    width: 8,
  },
  "&::-webkit-scrollbar-thumb": {
    backgroundColor: alpha(theme.palette.text.secondary, 0.35),
    borderRadius: 999,
  },
  "&::-webkit-scrollbar-track": {
    backgroundColor: "transparent",
  },
});

const softTimelineDividerSx = (theme) => ({
  borderTop: "1px solid",
  borderColor: alpha(theme.palette.text.secondary, 0.14),
});

const channelIconPalette = {
  blue: {
    background: ["blue", 500],
    color: ["common", "white"],
  },
  neutral: {
    background: ["gray", 500],
    color: ["common", "white"],
  },
  success: {
    background: ["green", 500],
    color: ["common", "white"],
  },
  warning: {
    background: ["orange", 500],
    color: ["common", "white"],
  },
};

const statusConfig = {
  accepted: { label: "Aceita", tone: "info", icon: AccessTimeRoundedIcon },
  clicked: { label: "Clicada", tone: "success", icon: DoneAllRoundedIcon },
  deferred: { label: "Em processamento", tone: "info", icon: AccessTimeRoundedIcon },
  delivered: { label: "Entregue", tone: "success", icon: DoneAllRoundedIcon },
  delivery_ack: { label: "Entregue", tone: "success", icon: DoneAllRoundedIcon },
  error: { label: "Falhou", tone: "error", icon: ErrorOutlineRoundedIcon },
  expired: { label: "Expirada", tone: "error", icon: ErrorOutlineRoundedIcon },
  failed: { label: "Falhou", tone: "error", icon: ErrorOutlineRoundedIcon },
  failure: { label: "Falhou", tone: "error", icon: ErrorOutlineRoundedIcon },
  in_progress: { label: "Em processamento", tone: "info", icon: AccessTimeRoundedIcon },
  not_delivered: { label: "Não entregue", tone: "error", icon: ErrorOutlineRoundedIcon },
  pending: { label: "Em processamento", tone: "info", icon: AccessTimeRoundedIcon },
  processing: { label: "Em processamento", tone: "info", icon: AccessTimeRoundedIcon },
  queued: { label: "Na fila", tone: "info", icon: AccessTimeRoundedIcon },
  read: { label: "Lida", tone: "success", icon: DoneAllRoundedIcon },
  rejected: { label: "Rejeitada", tone: "error", icon: ErrorOutlineRoundedIcon },
  scheduled: { label: "Agendada", tone: "info", icon: AccessTimeRoundedIcon },
  sent: { label: "Enviada", tone: "info", icon: DoneAllRoundedIcon },
  skipped_closed: { label: "Janela fechada", tone: "warning", icon: ErrorOutlineRoundedIcon },
  skipped_duplicate: { label: "Duplicada", tone: "warning", icon: ErrorOutlineRoundedIcon },
  verified: { label: "Verificada", tone: "success", icon: DoneAllRoundedIcon },
  waiting: { label: "Em processamento", tone: "info", icon: AccessTimeRoundedIcon },
};

const inboundTypeConfig = {
  audio: { label: "Áudio recebido" },
  button: { label: "Botão recebido" },
  contacts: { label: "Contato recebido" },
  document: { label: "Documento recebido" },
  image: { label: "Imagem recebida" },
  interactive: { label: "Interação recebida" },
  location: { label: "Localização recebida" },
  reaction: { label: "Reação recebida" },
  sticker: { label: "Figurinha recebida" },
  video: { label: "Vídeo recebido" },
};

function formatDateTime(value) {
  if (!value) return "-";

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);

  return dateFormatter.format(date).replace(",", "");
}

function formatDay(value) {
  if (!value) return "-";

  const date = new Date(`${value}T12:00:00`);
  if (Number.isNaN(date.getTime())) return String(value);

  return dayFormatter.format(date);
}

function formatShortDay(value) {
  if (!value) return "-";

  const date = new Date(`${value}T12:00:00`);
  if (Number.isNaN(date.getTime())) return String(value);

  return shortDayFormatter.format(date);
}

function formatTime(value) {
  if (!value) return "";

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";

  return timeFormatter.format(date);
}

function parseMessageDate(message) {
  const value = message?.occurred_at || message?.created_at || message?.updated_at;
  if (!value) return 0;

  const timestamp = new Date(value).getTime();
  return Number.isNaN(timestamp) ? 0 : timestamp;
}

function parseDayDate(dayGroup) {
  const firstItem = dayGroup?.items?.[0];
  const value = firstItem?.occurred_at || firstItem?.created_at || dayGroup?.day;
  if (!value) return 0;

  const timestamp = new Date(value).getTime();
  return Number.isNaN(timestamp) ? 0 : timestamp;
}

function initialsFromContact(contact) {
  const value = String(contact?.contact_point || contact?.contact_secondary || "C").trim();
  return value.slice(0, 2).toUpperCase();
}

function getMessageKey(message, index) {
  return (
    message?.id ||
    message?.timeline_id ||
    message?.source_ref ||
    message?.campaign_message_id ||
    message?.message_id ||
    `${message?.campaign_id || message?.timeline_type || "message"}-${message?.created_at || index}`
  );
}

function getCampaignMessageDetailKey(message) {
  const campaignExecutionId = message?.campaign_execution_id || message?.campaign_run_id;
  const itemId = message?.item_id || message?.campaign_message_id;
  const messageId = message?.message_id;

  if (!campaignExecutionId || (!itemId && !messageId)) return "";
  return [campaignExecutionId, itemId || "", messageId || ""].join("|");
}

function isCampaignDetailPending(item, messageDetailsByKey, messageDetailLoadingByKey) {
  if (!isCampaignMessage(item) || getMessageText(item)) return false;

  const detailKey = getCampaignMessageDetailKey(item);
  if (!detailKey) return false;

  const detailLoaded = Object.prototype.hasOwnProperty.call(messageDetailsByKey, detailKey);
  const detail = messageDetailsByKey[detailKey];

  return Boolean(messageDetailLoadingByKey[detailKey] || (!detailLoaded && !detail?.message_preview));
}

function getPreviewEntriesItems(entries = []) {
  return entries.flatMap((entry) => {
    if (!entry) return [];
    return Array.isArray(entry.items) ? entry.items : [entry];
  });
}

function hasPendingCampaignDetailsForEntries(
  entries,
  messageDetailsByKey,
  messageDetailLoadingByKey,
) {
  return getPreviewEntriesItems(entries).some((item) =>
    isCampaignDetailPending(item, messageDetailsByKey, messageDetailLoadingByKey),
  );
}

function getDayKey(dayGroup, index) {
  return getTimelineGroupId(dayGroup, index);
}

function getChannelPresentation(channelType) {
  return channelConfig[String(channelType || "").trim().toLowerCase()] || {
    icon: CampaignRoundedIcon,
    label: channelType || "Canal",
    tone: "neutral",
  };
}

function getPaletteToken(theme, token) {
  const [scale, shade] = token;
  if (scale === "common") return theme.palette.common?.[shade];
  return theme.palette.custom?.[scale]?.[shade];
}

function normalizeComparableContactValue(value) {
  const rawValue = String(value || "").trim().toLowerCase();
  if (!rawValue) return "";

  if (rawValue.includes("@")) return rawValue;

  return rawValue.replace(/\D/g, "");
}

function isContactPointTechnicalId(value) {
  return /^ctp_[a-z0-9]+$/i.test(String(value || "").trim());
}

function getReadableContactValue(...values) {
  return values.find((value) => {
    const text = String(value || "").trim();
    return text && !isContactPointTechnicalId(text);
  });
}

function isEquivalentContactValue(firstValue, secondValue) {
  const first = normalizeComparableContactValue(firstValue);
  const second = normalizeComparableContactValue(secondValue);

  if (!first || !second) return false;
  if (first === second) return true;
  if (first.includes("@") || second.includes("@")) return false;

  const shorter = first.length <= second.length ? first : second;
  const longer = first.length > second.length ? first : second;

  return shorter.length >= 8 && longer.endsWith(shorter);
}

function getContactHeader(contact) {
  const title = getReadableContactValue(
    contact?.contact_point,
    contact?.contact_secondary,
  ) || "Contato";
  const secondary = getReadableContactValue(contact?.contact_secondary) || "";

  return {
    subtitle:
      secondary && !isEquivalentContactValue(title, secondary)
        ? secondary
        : "Histórico de campanhas",
    title,
  };
}

function getMessageStatus(message) {
  const status = String(message?.latest_status || message?.status || message?.dispatch_result || "")
    .trim()
    .toLowerCase();
  const normalizedStatus = status.replace(/[\s-]+/g, "_");

  return statusConfig[normalizedStatus] || {
    label: status ? "Desconhecido" : "Registrada",
    tone: "neutral",
    icon: InfoOutlinedIcon,
  };
}

function hasMetadataValue(value) {
  if (value === null || value === undefined) return false;
  if (typeof value === "boolean") return true;
  return String(value).trim() !== "";
}

function firstMetadataValue(...values) {
  return values.find(hasMetadataValue);
}

function stringifyMetadataValue(value) {
  if (typeof value === "boolean") return value ? "Sim" : "Não";
  return String(value || "").trim();
}

function appendMetadataField(fields, label, value, options = {}) {
  if (!hasMetadataValue(value)) return;

  fields.push({
    copyable: options.copyable !== false,
    label,
    layout: options.layout || "full",
    value: stringifyMetadataValue(value),
  });
}

function appendMetadataDate(fields, label, value) {
  if (!hasMetadataValue(value)) return;

  appendMetadataField(fields, label, formatDateTime(value), { copyable: false });
}

function getDirectionLabel(direction) {
  const value = String(direction || "").trim().toLowerCase();
  if (value === "inbound") return "Recebida";
  if (value === "outbound") return "Enviada";
  if (value === "system") return "Sistema";
  return direction || "";
}

function getContactMetadataValue(contact) {
  return firstMetadataValue(
    contact?.contact_point,
    contact?.value_display,
    contact?.recipient_contact,
    contact?.contact_secondary,
  );
}

function getMetadataStatus(item, detail) {
  const statusSource = firstMetadataValue(
    detail?.meta_status?.code,
    detail?.delivery_status?.code,
    detail?.message_status_latest,
    item?.latest_status,
    item?.status,
    item?.dispatch_result,
  );

  if (!hasMetadataValue(statusSource)) return null;

  const status = getMessageStatus({ latest_status: statusSource });
  return {
    label: firstMetadataValue(
      detail?.meta_status?.label,
      detail?.delivery_status?.label,
      status.label,
    ),
    tone: status.tone,
  };
}

function buildMessageMetadata({ contact, detail, item }) {
  const isInbound = isInboundMessage(item);
  const isCampaign = isCampaignMessage(item);
  const channelType = firstMetadataValue(detail?.inbox_type, item?.channel_type);
  const channel = channelType ? getChannelPresentation(channelType) : null;
  const dates = [];
  const rows = [];
  const status = getMetadataStatus(item, detail);
  const contactValue = getContactMetadataValue(contact);
  const inboundChannelNumber = firstMetadataValue(
    item?.channel_identifier,
    item?.channel_display_number,
    item?.channel_phone_number,
    item?.display_phone_number,
    item?.meta_display_phone_number,
  );
  const inboundChannelId = firstMetadataValue(item?.channel_id, item?.instance);

  appendMetadataDate(dates, isInbound ? "Recebida" : "Registrada", item?.occurred_at || item?.created_at);
  if (isInbound) {
    appendMetadataField(dates, "Canal de entrada", inboundChannelNumber, { copyable: false });
  }
  appendMetadataDate(dates, "Criada", detail?.created_at || (!isInbound ? item?.created_at : null));

  appendMetadataField(rows, "Canal", channel?.label, { copyable: false, layout: "half" });
  appendMetadataField(rows, "Direção", getDirectionLabel(item?.direction), { copyable: false, layout: "half" });

  if (isInbound) {
    appendMetadataField(rows, "De", item?.recipient_contact || contactValue);
    appendMetadataField(rows, "Id do canal", inboundChannelId);
  } else {
    appendMetadataField(rows, "Para", detail?.recipient_contact || item?.recipient_contact || contactValue);
  }

  appendMetadataField(rows, "Id do item", detail?.item_id || item?.item_id || item?.campaign_message_id);
  appendMetadataField(rows, "Id da timeline", item?.timeline_id);
  appendMetadataField(rows, "Id do provedor", detail?.message_id || item?.message_id || item?.provider_message_id);

  if (isCampaign) {
    appendMetadataField(rows, "Campanha", item?.campaign_title, { copyable: false });
    appendMetadataField(rows, "Id da campanha", item?.campaign_id);
    appendMetadataField(rows, "Execução", detail?.campaign_execution_id || item?.campaign_execution_id || item?.campaign_run_id);
    appendMetadataField(rows, "Listagem", item?.listing_title, { copyable: false });
    appendMetadataField(rows, "Id da listagem", item?.listing_id);
  } else {
    appendMetadataField(rows, "Campanha relacionada", item?.related_campaign_title, { copyable: false });
    appendMetadataField(rows, "Id da campanha relacionada", item?.related_campaign_id);
    appendMetadataField(rows, "Execução relacionada", item?.related_campaign_run_id);
    appendMetadataField(rows, "Mensagem relacionada", item?.related_campaign_message_id);
    appendMetadataField(rows, "Listagem relacionada", item?.related_listing_title, { copyable: false });
    appendMetadataField(rows, "Id da listagem relacionada", item?.related_listing_id);
  }

  const mediaAsset = getMessageMediaAsset(item);
  if (mediaAsset) {
    appendMetadataField(rows, "Tipo da mídia", getMediaPresentation(mediaAsset).label, { copyable: false, layout: "half" });
    appendMetadataField(rows, "Status da mídia", getMediaStatusPresentation(mediaAsset.status).label, { copyable: false, layout: "half" });
    appendMetadataField(rows, "Id da mídia", mediaAsset.media_asset_id || mediaAsset.provider_media_id);
    appendMetadataField(rows, "Nome do arquivo", mediaAsset.filename, { copyable: false });
    appendMetadataField(rows, "MIME type", mediaAsset.mime_type, { layout: "half" });
    appendMetadataField(rows, "Tamanho", formatFileSize(mediaAsset.size_bytes), { copyable: false, layout: "half" });
    appendMetadataField(rows, "SHA-256", mediaAsset.sha256);
  }

  const contactCard = getMessageContactCard(item);
  if (contactCard) {
    appendMetadataField(rows, "Contato compartilhado", contactCard.name, { copyable: false });
    appendMetadataField(rows, "Telefone compartilhado", contactCard.phone, { layout: "half" });
  }

  appendMetadataField(rows, "Erro", detail?.error_detail || item?.error_message, { copyable: false });
  appendMetadataField(rows, "Código do erro", detail?.error_code || item?.error_code);
  appendMetadataField(rows, "Categoria do erro", detail?.error_category || item?.error_category, { copyable: false });

  return { dates, rows, status };
}

function isCampaignMessage(message) {
  const timelineType = String(message?.timeline_type || "").trim().toLowerCase();
  if (timelineType === "campaign_message") return true;
  if (timelineType === "inbound_message" || timelineType === "chatbot_event" || timelineType === "chatbot_message") {
    return false;
  }

  const rawType = String(
    message?.message_type ||
      message?.event_type ||
      message?.direction ||
      message?.source ||
      "",
  )
    .trim()
    .toLowerCase();

  if (rawType.includes("inbound") || rawType.includes("received")) return false;
  if (rawType.includes("campaign")) return true;

  return Boolean(
    message?.campaign_id ||
      message?.campaign_title ||
      message?.campaign_message_id ||
      message?.campaign_run_id ||
      message?.campaign_execution_id,
  );
}

function isInboundMessage(message) {
  const timelineType = String(message?.timeline_type || "").trim().toLowerCase();
  if (timelineType === "inbound_message") return true;

  return String(message?.direction || "").trim().toLowerCase() === "inbound";
}

function isChatbotMessage(message) {
  return String(message?.timeline_type || "").trim().toLowerCase() === "chatbot_message";
}

function getInboundTypePresentation(message) {
  const type = String(message?.message_type || "").trim().toLowerCase();
  return inboundTypeConfig[type] || null;
}

function getInboundMetadataText(message) {
  const locationText = [message?.location_name, message?.location_address]
    .filter(Boolean)
    .join(" - ");
  const contactCard = getMessageContactCard(message);

  return String(
    message?.interactive_title ||
      message?.button_text ||
      message?.reaction_emoji ||
      message?.media_caption ||
      [contactCard?.name, contactCard?.phone].filter(Boolean).join(" - ") ||
      locationText ||
    "",
  ).trim();
}

function normalizeChatbotEventTitle(title) {
  return String(title || "")
    .replace(/^Chatbot\s+Chatbot\s+/i, "Chatbot ")
    .trim();
}

function getMessageTitle(message) {
  if (isCampaignMessage(message)) {
    return message.campaign_title || message.title || "Campanha sem título";
  }

  if (String(message?.timeline_type || "").trim().toLowerCase() === "chatbot_event") {
    return normalizeChatbotEventTitle(message?.title) || "Evento de chatbot";
  }

  return message?.title || message?.sender_name || "Mensagem recebida";
}

function getMessageDescription(message) {
  if (isCampaignMessage(message)) {
    return message.listing_title || message.description || "Sem listagem vinculada";
  }

  const typePresentation = getInboundTypePresentation(message);
  const description = String(message?.description || "").trim();
  const text = getMessageText(message);
  if (description && description !== "Mensagem recebida") {
    return description;
  }
  if (text) {
    return text;
  }
  if (typePresentation) {
    return typePresentation.label;
  }

  return description || "Mensagem recebida pelo contato";
}

function messageMatchesSearch(message, searchTerm) {
  const query = String(searchTerm || "").trim().toLowerCase();
  if (!query) return true;

  const channel = getChannelPresentation(message.channel_type);
  const status = getMessageStatus(message);
  const mediaAsset = getMessageMediaAsset(message);
  const contactCard = getMessageContactCard(message);
  const searchable = [
    getMessageTitle(message),
    getMessageDescription(message),
    message?.message_text,
    message?.text_preview,
    message?.body_text,
    message?.text,
    channel.label,
    status.label,
    mediaAsset ? getMediaPresentation(mediaAsset).label : "",
    mediaAsset ? getMediaStatusPresentation(mediaAsset.status).label : "",
    mediaAsset?.filename,
    mediaAsset?.caption,
    mediaAsset?.mime_type,
    contactCard?.name,
    contactCard?.phone,
    formatDateTime(message.occurred_at || message.created_at),
  ]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();

  return searchable.includes(query);
}

function getMessageText(message) {
  return String(
    message?.message_text ||
      message?.text_preview ||
      message?.body_text ||
      message?.text ||
      getInboundMetadataText(message) ||
      "",
  ).trim();
}

function getDetailPreviewText(detail) {
  return getDeliveredPreviewText(detail?.message_preview);
}

function getTimelineMessageText(item, detail) {
  return getMessageText(item) || getDetailPreviewText(detail);
}

function getDetailError(detail, message) {
  return (
    detail?.error_detail ||
    detail?.error_message ||
    detail?.message_status_meta_error_message ||
    detail?.dispatch_error ||
    message?.error_message ||
    message?.error_category ||
    message?.error_code ||
    ""
  );
}

function buildLedgerPreview(message) {
  const text = getMessageText(message);
  const error = getDetailError(null, message);

  if (!text && !error) return null;

  return {
    channel: message?.channel_type,
    error,
    sent_at: message?.updated_at || message?.created_at,
    status: message?.latest_status || message?.dispatch_result,
    text,
  };
}

function buildTimelinePreview(message, detail) {
  const text = getTimelineMessageText(message, detail);
  const error = getDetailError(detail, message);

  if (!text && !error) return null;

  return {
    channel: detail?.inbox_type || message?.channel_type,
    error,
    sent_at: detail?.created_at || message?.occurred_at || message?.created_at || message?.updated_at,
    status: detail?.meta_status?.label || message?.latest_status || message?.status || message?.dispatch_result,
    text,
  };
}

function buildUnavailablePreview(message, detail) {
  return {
    channel: detail?.inbox_type || message?.channel_type,
    error: "Conteúdo da mensagem indisponível.",
  };
}

function MessageListItem({ active, message, onClick }) {
  const channel = getChannelPresentation(message.channel_type);
  const ChannelIcon = channel.icon;
  const title = getMessageTitle(message);
  const description = getMessageDescription(message);

  return (
    <Box
      component="button"
      onClick={onClick}
      type="button"
      sx={(theme) => ({
        appearance: "none",
        backgroundColor: active ? alpha(theme.palette.primary.main, 0.08) : "transparent",
        border: "0",
        borderBottom: "1px solid",
        borderColor: "border.muted",
        boxSizing: "border-box",
        color: "inherit",
        cursor: "pointer",
        display: "block",
        font: "inherit",
        m: 0,
        px: 2,
        py: 1.5,
        textAlign: "left",
        width: "100%",
        "&:hover": {
          backgroundColor: theme.palette.action.hover,
        },
        "&:focus-visible": {
          outline: `2px solid ${theme.palette.primary.main}`,
          outlineOffset: -2,
        },
      })}
    >
      <Stack alignItems="center" direction="row" spacing={1.25}>
        <Box
          sx={(theme) => ({
            alignItems: "center",
            backgroundColor: active
              ? alpha(theme.palette.primary.main, 0.1)
              : theme.palette.surface.subtle,
            borderRadius: "8px",
            color: active ? theme.palette.primary.main : theme.palette.text.secondary,
            display: "inline-flex",
            flexShrink: 0,
            height: 40,
            justifyContent: "center",
            position: "relative",
            width: 40,
          })}
        >
          <CalendarMonthRoundedIcon sx={{ fontSize: 21 }} />
          <Box
            component="span"
            sx={(theme) => {
              const palette = channelIconPalette[channel.tone] || channelIconPalette.neutral;

              return {
                alignItems: "center",
                backgroundColor: getPaletteToken(theme, palette.background),
                border: `2px solid ${theme.palette.background.paper}`,
                borderRadius: "999px",
                bottom: -3,
                color: getPaletteToken(theme, palette.color),
                display: "inline-flex",
                height: 17,
                justifyContent: "center",
                position: "absolute",
                right: -3,
                width: 17,
              };
            }}
          >
            <ChannelIcon sx={{ fontSize: 10.5 }} />
          </Box>
        </Box>

        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            sx={{
              color: "text.primary",
              fontSize: 13,
              fontWeight: 800,
              lineHeight: 1.3,
              minWidth: 0,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {title}
          </Typography>

          <Typography
            sx={{
              color: "text.secondary",
              fontSize: 12,
              lineHeight: 1.35,
              mt: 0.25,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {description}
          </Typography>
        </Box>
      </Stack>
    </Box>
  );
}

function getDaySummary(dayGroup) {
  const items = Array.isArray(dayGroup?.items) ? dayGroup.items : [];
  const total = items.filter((item) => {
    const timelineType = String(item?.timeline_type || "").trim().toLowerCase();
    const direction = String(item?.direction || "").trim().toLowerCase();
    return timelineType !== "chatbot_event" && timelineType !== "system_event" && direction !== "system";
  }).length;

  return `${total} interaç${total === 1 ? "ão" : "ões"}`;
}

function dayMatchesSearch(dayGroup, searchTerm) {
  const query = String(searchTerm || "").trim().toLowerCase();
  if (!query) return true;

  const searchable = [
    formatDay(dayGroup?.day),
    formatShortDay(dayGroup?.day),
    getDaySummary(dayGroup),
    ...(dayGroup?.items || []).flatMap((item) => [
      getMessageTitle(item),
      getMessageDescription(item),
      getMessageText(item),
      getChannelPresentation(item.channel_type).label,
      getMessageStatus(item).label,
      formatDateTime(item.occurred_at || item.created_at),
    ]),
  ]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();

  return searchable.includes(query);
}

function filterDayGroupItems(dayGroup, channelFilter, searchTerm) {
  const items = Array.isArray(dayGroup?.items) ? dayGroup.items : [];
  const filteredItems = items.filter((item) => {
    const matchesChannel =
      channelFilter === "all" || item.channel_type === channelFilter;
    return matchesChannel && messageMatchesSearch(item, searchTerm);
  });

  return {
    ...dayGroup,
    items: filteredItems,
  };
}

function TimelineDayListItem({ active, dayGroup, onClick }) {
  const items = Array.isArray(dayGroup?.items) ? dayGroup.items : [];
  const firstChannel = getChannelPresentation(items[0]?.channel_type);
  const ChannelIcon = firstChannel.icon;

  return (
    <Box
      component="button"
      onClick={onClick}
      type="button"
      sx={(theme) => ({
        appearance: "none",
        backgroundColor: active ? alpha(theme.palette.primary.main, 0.08) : "transparent",
        border: "0",
        borderBottom: "1px solid",
        borderColor: "border.muted",
        boxSizing: "border-box",
        color: "inherit",
        cursor: "pointer",
        display: "block",
        font: "inherit",
        m: 0,
        px: 2,
        py: 1.5,
        textAlign: "left",
        width: "100%",
        "&:hover": {
          backgroundColor: theme.palette.action.hover,
        },
        "&:focus-visible": {
          outline: `2px solid ${theme.palette.primary.main}`,
          outlineOffset: -2,
        },
      })}
    >
      <Stack alignItems="center" direction="row" spacing={1.25}>
        <Box
          sx={(theme) => ({
            alignItems: "center",
            backgroundColor: active
              ? alpha(theme.palette.primary.main, 0.1)
              : theme.palette.surface.subtle,
            borderRadius: "8px",
            color: active ? theme.palette.primary.main : theme.palette.text.secondary,
            display: "inline-flex",
            flexShrink: 0,
            height: 40,
            justifyContent: "center",
            position: "relative",
            width: 40,
          })}
        >
          <CalendarMonthRoundedIcon sx={{ fontSize: 21 }} />
          <Box
            component="span"
            sx={(theme) => {
              const palette = channelIconPalette[firstChannel.tone] || channelIconPalette.neutral;

              return {
                alignItems: "center",
                backgroundColor: getPaletteToken(theme, palette.background),
                border: `2px solid ${theme.palette.background.paper}`,
                borderRadius: "999px",
                bottom: -3,
                color: getPaletteToken(theme, palette.color),
                display: "inline-flex",
                height: 17,
                justifyContent: "center",
                position: "absolute",
                right: -3,
                width: 17,
              };
            }}
          >
            <ChannelIcon sx={{ fontSize: 10.5 }} />
          </Box>
        </Box>

        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            sx={{
              color: "text.primary",
              fontSize: 13,
              fontWeight: 800,
              lineHeight: 1.3,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {formatShortDay(dayGroup?.day)}
          </Typography>
          <Typography
            sx={{
              color: "text.secondary",
              fontSize: 12,
              lineHeight: 1.35,
              mt: 0.25,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {getDaySummary(dayGroup)}
          </Typography>
        </Box>
      </Stack>
    </Box>
  );
}

function EmptyMessageIllustration() {
  return (
    <Box
      aria-hidden
      component="svg"
      viewBox="0 0 96 72"
      sx={{
        color: "text.secondary",
        height: 82,
        mb: 1.5,
        opacity: 0.68,
        width: 108,
      }}
    >
      <path
        d="M48 10C29.8 10 15 21.1 15 34.8c0 8.4 5.5 15.7 13.9 20.2l-4.1 9.2 12.1-5.7c3.5.7 7.2 1.1 11.1 1.1 18.2 0 33-11.1 33-24.8S66.2 10 48 10Z"
        fill="none"
        stroke="currentColor"
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth="1.8"
      />
    </Box>
  );
}

function MessagePreviewLoading() {
  return (
    <Box
      sx={{ flex: 1, minHeight: 220, width: "100%" }}
    />
  );
}

function MessageHistoryChatLoading() {
  return (
    <Box
      sx={(theme) => ({
        alignItems: "center",
        backgroundColor: theme.palette.surface.muted,
        color: "rgb(100, 116, 139)",
        display: "flex",
        flex: 1,
        flexDirection: "column",
        justifyContent: "center",
        minHeight: 0,
        width: "100%",
      })}
    >
      <Box
        aria-hidden
        component="svg"
        viewBox="0 0 24 24"
        sx={{
          color: "inherit",
          height: 96,
          width: 96,
        }}
      >
        <path
          d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83"
          fill="none"
          stroke="currentColor"
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth="2"
        />
      </Box>
      <Typography
        sx={{
          color: "inherit",
          fontSize: 20,
          fontWeight: 600,
          lineHeight: "30px",
          mt: 2,
        }}
      >
        Carregando mensagens...
      </Typography>
    </Box>
  );
}

function MessageHistoryContentReveal({ children, revealKey }) {
  return (
    <Box
      key={revealKey}
      sx={{
        "@keyframes messageHistoryContentReveal": {
          "0%": {
            opacity: 0,
            transform: "translateY(4px)",
          },
          "100%": {
            opacity: 1,
            transform: "translateY(0)",
          },
        },
        animation: "messageHistoryContentReveal 140ms ease-out both",
        display: "flex",
        flex: 1,
        minHeight: 0,
        minWidth: 0,
        width: "100%",
        willChange: "opacity, transform",
        "@media (prefers-reduced-motion: reduce)": {
          animation: "none",
          transform: "none",
        },
      }}
    >
      {children}
    </Box>
  );
}

function TimelineEventDivider({ channelLabel, isCampaign, occurredAt }) {
  return (
    <Box
      sx={{
        alignItems: "center",
        display: "grid",
        gap: { xs: 1, sm: 2 },
        gridTemplateColumns: {
          xs: "24px minmax(0, 1fr) 24px",
          sm: "minmax(24px, 1fr) auto minmax(24px, 1fr)",
        },
        width: "100%",
      }}
    >
      <Box sx={softTimelineDividerSx} />
      <Stack alignItems="center" direction="row" spacing={1.25} sx={{ minWidth: 0 }}>
        <Box
          sx={(theme) => ({
            alignItems: "center",
            color: theme.palette.custom.blue[600],
            display: "inline-flex",
            flexShrink: 0,
            justifyContent: "center",
          })}
        >
          <CampaignRoundedIcon sx={{ fontSize: 19 }} />
        </Box>
        <Typography
          sx={{
            color: "text.secondary",
            fontSize: 12,
            lineHeight: 1.45,
            textAlign: "center",
            whiteSpace: { xs: "normal", sm: "nowrap" },
          }}
        >
          {isCampaign ? "Campanha enviada em" : "Mensagem recebida em"}{" "}
          <Box component="strong" sx={{ color: "text.primary", fontWeight: 800 }}>
            {formatDateTime(occurredAt)}
          </Box>
          {" "}no canal {channelLabel}
        </Typography>
      </Stack>
      <Box sx={softTimelineDividerSx} />
    </Box>
  );
}

function TimelineMessagePreviewEntry({
  contact,
  contactPointId,
  detail,
  detailLoading = false,
  item,
  onLoadMediaAccess,
}) {
  if (detailLoading && !detail) return <MessagePreviewLoading />;

  const channel = getChannelPresentation(item.channel_type);
  const isCampaign = isCampaignMessage(item);

  return (
    <Fragment>
      <TimelineEventDivider
        channelLabel={channel.label}
        isCampaign={isCampaign}
        occurredAt={item.occurred_at || item.created_at}
      />
      <TimelineMessageBubble
        contact={contact}
        contactPointId={contactPointId}
        detail={detail}
        item={item}
        onLoadMediaAccess={onLoadMediaAccess}
      />
    </Fragment>
  );
}

function MessagePreview({
  contact,
  contactPointId,
  detail,
  detailLoading,
  hasPreviousMessages = false,
  loadingPreviousMessages = false,
  message,
  messageDetailLoadingByKey = {},
  messageDetailsByKey = {},
  messages = [],
  onLoadMediaAccess,
  onLoadPreviousMessages,
}) {
  const visiblePreviewMessages = messages.length ? messages.filter(Boolean) : message ? [message] : [];

  if (!message || !visiblePreviewMessages.length) {
    return (
      <Box
        sx={{
          alignItems: "center",
          display: "flex",
          flex: 1,
          flexDirection: "column",
          justifyContent: "center",
          p: 3,
          textAlign: "center",
        }}
      >
        <EmptyMessageIllustration />
        <Typography sx={{ color: "text.secondary", fontSize: 13, fontWeight: 600 }}>
          Escolha um item para visualizar a mensagem.
        </Typography>
      </Box>
    );
  }

  const title = getMessageTitle(message);
  const description = getMessageDescription(message);
  const selectedMessageKey = getMessageKey(message, 0);

  return (
    <Box sx={{ display: "flex", flex: 1, flexDirection: "column", minHeight: 0, minWidth: 0, overflow: "hidden" }}>
      <Box
        sx={{
          borderBottom: "1px solid",
          borderColor: "border.default",
          px: { xs: 2, md: 3 },
          py: 2,
        }}
      >
        <Stack spacing={1.25}>
          <Stack alignItems="center" direction="row" spacing={2}>
            <Box sx={{ minWidth: 0, width: "100%" }}>
              <Typography
                sx={{
                  color: "text.primary",
                  fontSize: 16,
                  fontWeight: 600,
                  lineHeight: 1.25,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {title}
              </Typography>
              <Typography sx={{ color: "text.secondary", fontSize: 12, mt: 0.25 }}>
                {description}
              </Typography>
            </Box>
          </Stack>
        </Stack>
      </Box>

      <Box
        sx={(theme) => ({
          backgroundColor: theme.palette.surface.muted,
          display: "flex",
          flex: 1,
          flexDirection: "column",
          gap: 2,
          minHeight: 0,
          px: { xs: 2, md: 3 },
          py: 3,
          ...chatScrollAreaSx(theme),
        })}
      >
        {hasPreviousMessages ? (
          <TimelinePreviousConversationButton
            disabled={loadingPreviousMessages}
            onClick={onLoadPreviousMessages}
          />
        ) : null}

        {visiblePreviewMessages.map((entry, index) => {
          const entryKey = getMessageKey(entry, index);
          const isSelectedEntry = entry === message || entryKey === selectedMessageKey;
          const detailKey = getCampaignMessageDetailKey(entry);
          const entryDetail = isSelectedEntry
            ? detail
            : detailKey
              ? messageDetailsByKey[detailKey]
              : null;
          const entryDetailResolved = detailKey
            ? Object.prototype.hasOwnProperty.call(messageDetailsByKey, detailKey)
            : true;
          const entryDetailLoading = isSelectedEntry
            ? Boolean(detailLoading || (detailKey && !entryDetailResolved && !entryDetail))
            : Boolean(
                detailKey &&
                  (!entryDetailResolved || messageDetailLoadingByKey[detailKey]) &&
                  !entryDetail,
              );

          return (
            <TimelineMessagePreviewEntry
              contact={contact}
              contactPointId={contactPointId}
              detail={entryDetail}
              detailLoading={entryDetailLoading}
              item={entry}
              key={entryKey}
              onLoadMediaAccess={onLoadMediaAccess}
            />
          );
        })}

      </Box>
    </Box>
  );
}

function TimelineDayDivider({ day }) {
  return (
    <Box
      sx={{
        alignItems: "center",
        display: "grid",
        gap: { xs: 1, sm: 2 },
        gridTemplateColumns: {
          xs: "24px minmax(0, 1fr) 24px",
          sm: "minmax(24px, 1fr) auto minmax(24px, 1fr)",
        },
        width: "100%",
      }}
    >
      <Box sx={softTimelineDividerSx} />
      <Typography
        sx={{
          color: "text.secondary",
          fontSize: 12,
          fontWeight: 700,
          lineHeight: 1.45,
          textAlign: "center",
          whiteSpace: "nowrap",
        }}
      >
        {formatDay(day)}
      </Typography>
      <Box sx={softTimelineDividerSx} />
    </Box>
  );
}

function TimelineSystemEvent({ item }) {
  const title = getMessageTitle(item);
  const description = getMessageDescription(item);

  return (
    <Box
      sx={{
        alignItems: "center",
        display: "grid",
        gap: { xs: 1, sm: 2 },
        gridTemplateColumns: {
          xs: "24px minmax(0, 1fr) 24px",
          sm: "minmax(24px, 1fr) auto minmax(24px, 1fr)",
        },
        width: "100%",
      }}
    >
      <Box sx={softTimelineDividerSx} />
      <Stack alignItems="center" direction="row" spacing={1.25} sx={{ minWidth: 0 }}>
        <Box
          sx={(theme) => ({
            alignItems: "center",
            color: theme.palette.custom.blue[600],
            display: "inline-flex",
            flexShrink: 0,
            justifyContent: "center",
          })}
        >
          <CampaignRoundedIcon sx={{ fontSize: 18 }} />
        </Box>
        <Typography
          sx={{
            color: "text.secondary",
            fontSize: 12,
            lineHeight: 1.45,
            textAlign: "center",
            whiteSpace: { xs: "normal", md: "nowrap" },
          }}
        >
          <Box component="strong" sx={{ color: "text.primary", fontWeight: 800 }}>
            {title}
          </Box>
          {description ? ` · ${description}` : ""}{" "}
          {formatTime(item?.occurred_at || item?.created_at)}
        </Typography>
      </Stack>
      <Box sx={softTimelineDividerSx} />
    </Box>
  );
}

function TimelinePreviousConversationButton({
  disabled = false,
  onClick,
}) {
  const label = disabled ? "Carregando mensagens anteriores" : "Ver mensagens anteriores";

  return (
    <Box sx={{ display: "flex", justifyContent: "center", width: "100%" }}>
      <Tooltip placement="top" title={label}>
        <span>
          <IconButton
            aria-label={label}
            disabled={disabled || !onClick}
            onClick={onClick}
            size="small"
            sx={(theme) => ({
              backgroundColor: theme.palette.background.paper,
              border: "1px solid",
              borderColor: alpha(theme.palette.text.secondary, 0.18),
              color: theme.palette.text.secondary,
              height: 30,
              width: 30,
              "&:hover": {
                backgroundColor: alpha(theme.palette.primary.main, 0.04),
                borderColor: alpha(theme.palette.primary.main, 0.24),
                color: theme.palette.primary.main,
              },
              "&.Mui-disabled": {
                backgroundColor: theme.palette.background.paper,
                borderColor: alpha(theme.palette.text.secondary, 0.12),
                color: alpha(theme.palette.text.secondary, 0.42),
              },
            })}
          >
            <KeyboardArrowUpRoundedIcon
              sx={{
                "@keyframes previousMessagesArrowSpin": {
                  "0%": { transform: "rotate(0deg)" },
                  "100%": { transform: "rotate(360deg)" },
                },
                animation: disabled ? "previousMessagesArrowSpin 0.9s linear infinite" : "none",
                fontSize: 20,
              }}
            />
          </IconButton>
        </span>
      </Tooltip>
    </Box>
  );
}

function TimelineInboundMessagePreview({
  contact,
  contactPointId,
  description,
  inboundType,
  item,
  onLoadMediaAccess,
  text,
}) {
  const content = text ||
    inboundType?.label ||
    (description && description !== "Mensagem recebida" ? description : "") ||
    "Mensagem sem conteúdo de texto";
  const hasContactCard = hasMessageContactCard(item);
  const hasMedia = hasMessageMedia(item);

  if (hasContactCard) {
    return (
      <Box sx={{ display: "flex", justifyContent: "flex-start", width: "100%" }}>
        <Box sx={timelineMessageFrameSx}>
          <TimelineContactCard
            direction="user"
            item={item}
            reserveTopActionSpace
            timestamp={formatTime(item?.occurred_at || item?.created_at)}
          />
          <MessageMetadataMenu metadata={buildMessageMetadata({ contact, item })} />
        </Box>
      </Box>
    );
  }

  if (hasMedia) {
    return (
      <Box sx={{ display: "flex", justifyContent: "flex-start", width: "100%" }}>
        <Box sx={timelineMessageFrameSx}>
          <TimelineMediaAttachment
            contactPointId={contactPointId}
            direction="user"
            item={item}
            onLoadMediaAccess={onLoadMediaAccess}
            reserveTopActionSpace
            timestamp={formatTime(item?.occurred_at || item?.created_at)}
          />
          <MessageMetadataMenu metadata={buildMessageMetadata({ contact, item })} />
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ display: "flex", justifyContent: "flex-start", width: "100%" }}>
      <Box
        sx={timelineMessageFrameSx}
      >
        <DeliveredMessagePreview
          fallbackChannel={item?.channel_type}
          fallbackSentAt={item?.occurred_at || item?.created_at}
          preview={{
            channel: item?.channel_type,
            sent_at: item?.occurred_at || item?.created_at,
            text: content,
          }}
          reserveTopActionSpace
          showStatusIcon={false}
          sx={userPreviewSurfaceSx}
        />
        <MessageMetadataMenu metadata={buildMessageMetadata({ contact, item })} />
      </Box>
    </Box>
  );
}

function TimelineMessageBubble({
  contact,
  contactPointId,
  detail,
  item,
  onLoadMediaAccess,
}) {
  const isCampaign = isCampaignMessage(item);
  const isInbound = isInboundMessage(item);
  const isChatbot = isChatbotMessage(item);
  const direction = String(item?.direction || "").trim().toLowerCase();
  const isOutbound = isCampaign || direction === "outbound";
  const channel = getChannelPresentation(item.channel_type);
  const hasMedia = hasMessageMedia(item);
  const mediaDirection = isOutbound ? "company" : "user";
  const status = getMessageStatus(item);
  const text = getTimelineMessageText(item, detail);
  const title = getMessageTitle(item);
  const description = getMessageDescription(item);
  const inboundType = getInboundTypePresentation(item);

  if (isCampaign) {
    const detailPreview = detail?.message_preview || null;
    const ledgerPreview = buildLedgerPreview(item);
    const preview = detailPreview || ledgerPreview;

    return (
      <Box sx={{ display: "flex", justifyContent: "flex-end", width: "100%" }}>
        <Box sx={timelineMessageFrameSx}>
          {preview ? (
            <>
              <DeliveredMessagePreview
                fallbackChannel={detail?.inbox_type || item.channel_type}
                fallbackError={getDetailError(detail, item)}
                fallbackSentAt={detail?.created_at || item.occurred_at || item.created_at}
                fallbackStatus={detail?.meta_status?.label || item.latest_status || item.status || item.dispatch_result}
                preview={preview}
                reserveTopActionSpace
                recipientContact={detail?.recipient_contact || item.recipient_contact}
                sx={companyPreviewSurfaceSx}
                variables={detail?.variables}
              />
              {hasMedia ? (
                <TimelineMediaAttachment
                  contactPointId={contactPointId}
                  direction={mediaDirection}
                  item={item}
                  onLoadMediaAccess={onLoadMediaAccess}
                  separated
                  timestamp={formatTime(item?.occurred_at || item?.created_at)}
                />
              ) : null}
              <MessageMetadataMenu metadata={buildMessageMetadata({ contact, detail, item })} />
            </>
          ) : (
            <>
              <DeliveredMessagePreview
                fallbackChannel={detail?.inbox_type || item.channel_type}
                preview={buildUnavailablePreview(item, detail)}
                reserveTopActionSpace
                showStatusIcon={false}
                sx={unavailablePreviewSurfaceSx}
              />
              {hasMedia ? (
                <TimelineMediaAttachment
                  contactPointId={contactPointId}
                  direction={mediaDirection}
                  item={item}
                  onLoadMediaAccess={onLoadMediaAccess}
                  separated
                  timestamp={formatTime(item?.occurred_at || item?.created_at)}
                />
              ) : null}
              <MessageMetadataMenu metadata={buildMessageMetadata({ contact, detail, item })} />
            </>
          )}
        </Box>
      </Box>
    );
  }

  if (isInbound) {
    return (
      <TimelineInboundMessagePreview
        contact={contact}
        contactPointId={contactPointId}
        description={description}
        inboundType={inboundType}
        item={item}
        onLoadMediaAccess={onLoadMediaAccess}
        text={text}
      />
    );
  }

  if (isChatbot) {
    const preview = buildTimelinePreview(item, detail);

    return (
      <Box sx={{ display: "flex", justifyContent: "flex-end", width: "100%" }}>
        <Box sx={timelineMessageFrameSx}>
          {preview ? (
            <>
              <DeliveredMessagePreview
                fallbackChannel={detail?.inbox_type || item.channel_type}
                fallbackError={getDetailError(detail, item)}
                fallbackSentAt={detail?.created_at || item.occurred_at || item.created_at}
                fallbackStatus={detail?.meta_status?.label || item.latest_status || item.status || item.dispatch_result}
                preview={preview}
                reserveTopActionSpace
                recipientContact={detail?.recipient_contact || item.recipient_contact}
                sx={companyPreviewSurfaceSx}
                variables={detail?.variables}
              />
              {hasMedia ? (
                <TimelineMediaAttachment
                  contactPointId={contactPointId}
                  direction={mediaDirection}
                  item={item}
                  onLoadMediaAccess={onLoadMediaAccess}
                  separated
                  timestamp={formatTime(item?.occurred_at || item?.created_at)}
                />
              ) : null}
              <MessageMetadataMenu metadata={buildMessageMetadata({ contact, detail, item })} />
            </>
          ) : (
            <>
              <DeliveredMessagePreview
                fallbackChannel={detail?.inbox_type || item.channel_type}
                preview={buildUnavailablePreview(item, detail)}
                reserveTopActionSpace
                showStatusIcon={false}
                sx={unavailablePreviewSurfaceSx}
              />
              {hasMedia ? (
                <TimelineMediaAttachment
                  contactPointId={contactPointId}
                  direction={mediaDirection}
                  item={item}
                  onLoadMediaAccess={onLoadMediaAccess}
                  separated
                  timestamp={formatTime(item?.occurred_at || item?.created_at)}
                />
              ) : null}
              <MessageMetadataMenu metadata={buildMessageMetadata({ contact, detail, item })} />
            </>
          )}
        </Box>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: isOutbound ? "flex-end" : "flex-start",
        width: "100%",
      }}
    >
      <Box
        sx={(theme) => {
          const bubbleStyle = getMessageBubbleStyle(theme, isOutbound ? "company" : "user");

          return {
            backgroundColor: bubbleStyle.backgroundColor,
            border: "1px solid",
            borderColor: bubbleStyle.borderColor,
            borderRadius: "8px",
            maxWidth: timelineMessageMaxWidthSx,
            minWidth: { xs: "min(100%, 180px)", sm: 220 },
            position: "relative",
            px: 1.5,
            pr: 4.5,
            py: 1.25,
            width: { xs: "100%", sm: "fit-content" },
          };
        }}
      >
        <Stack alignItems="flex-start" direction="row" justifyContent="space-between" spacing={1.5}>
          <Box sx={{ minWidth: 0 }}>
            <Typography
              sx={{
                color: "text.primary",
                fontSize: 13,
                fontWeight: 800,
                lineHeight: 1.35,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {title}
            </Typography>
            {description && description !== title ? (
              <Typography sx={{ color: "text.secondary", fontSize: 12, lineHeight: 1.35, mt: 0.25 }}>
                {description}
              </Typography>
            ) : null}
          </Box>
          <BadgeBase size="sm" tone={channel.tone}>
            {channel.label}
          </BadgeBase>
        </Stack>

        {text ? (
          <Typography
            sx={{
              color: "text.primary",
              fontSize: 13,
              lineHeight: 1.55,
              mt: 1,
              whiteSpace: "pre-wrap",
              wordBreak: "break-word",
            }}
          >
            {text}
          </Typography>
        ) : null}

        <Stack alignItems="center" direction="row" justifyContent="flex-end" spacing={0.75} sx={{ mt: 1 }}>
          {item?.status || item?.latest_status || item?.dispatch_result ? (
            <Typography sx={{ color: "text.secondary", fontSize: 11 }}>
              {status.label}
            </Typography>
          ) : null}
          <Typography sx={{ color: "text.secondary", fontSize: 11 }}>
            {formatTime(item?.occurred_at || item?.created_at)}
          </Typography>
        </Stack>
        <MessageMetadataMenu metadata={buildMessageMetadata({ contact, detail, item })} />
      </Box>
    </Box>
  );
}

function TimelineDayPreview({
  contact,
  contactPointId,
  dayGroup,
  dayGroups = [],
  hasPreviousMessages = false,
  loadingPreviousMessages = false,
  messageDetailLoadingByKey = {},
  messageDetailsByKey = {},
  onLoadMediaAccess,
  onLoadPreviousMessages,
}) {
  const visibleDayGroups = useMemo(
    () => (dayGroups.length ? dayGroups : dayGroup ? [dayGroup] : []).filter(Boolean),
    [dayGroup, dayGroups],
  );
  if (!visibleDayGroups.length) {
    return (
      <Box
        sx={{
          alignItems: "center",
          display: "flex",
          flex: 1,
          flexDirection: "column",
          justifyContent: "center",
          p: 3,
          textAlign: "center",
        }}
      >
        <EmptyMessageIllustration />
        <Typography sx={{ color: "text.secondary", fontSize: 13, fontWeight: 600 }}>
          Escolha um dia para visualizar o histórico.
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ display: "flex", flex: 1, flexDirection: "column", minHeight: 0, minWidth: 0, overflow: "hidden" }}>
      <Box
        sx={(theme) => ({
          backgroundColor: theme.palette.surface.muted,
          display: "flex",
          flex: 1,
          flexDirection: "column",
          gap: 2,
          minHeight: 0,
          px: { xs: 2, md: 3 },
          py: 3,
          ...chatScrollAreaSx(theme),
        })}
      >
        {hasPreviousMessages ? (
          <TimelinePreviousConversationButton
            disabled={loadingPreviousMessages}
            onClick={onLoadPreviousMessages}
          />
        ) : null}

        {visibleDayGroups.map((group, groupIndex) => {
          const groupKey = getDayKey(group, groupIndex);
          const groupItems = group?.items || [];

          return (
            <Fragment key={groupKey}>
              <TimelineDayDivider day={group.day} />

              {groupItems.map((item, itemIndex) => {
                const timelineType = String(item?.timeline_type || "");
                const isSystem = timelineType === "chatbot_event" || timelineType === "system_event";
                const itemKey = `${groupKey}-${getMessageKey(item, itemIndex)}`;
                const detailKey = getCampaignMessageDetailKey(item);
                const detail = detailKey ? messageDetailsByKey[detailKey] : null;
                const detailResolved = detailKey
                  ? Object.prototype.hasOwnProperty.call(messageDetailsByKey, detailKey)
                  : true;
                const detailLoading = Boolean(
                  detailKey &&
                    (!detailResolved || messageDetailLoadingByKey[detailKey]) &&
                    !detail,
                );

                return isSystem ? (
                  <TimelineSystemEvent item={item} key={itemKey} />
                ) : detailLoading ? (
                  <MessagePreviewLoading key={itemKey} />
                ) : (
                  <TimelineMessageBubble
                    contact={contact}
                    contactPointId={contactPointId}
                    detail={detail}
                    item={item}
                    key={itemKey}
                    onLoadMediaAccess={onLoadMediaAccess}
                  />
                );
              })}
            </Fragment>
          );
        })}
      </Box>
    </Box>
  );
}

function MessageHistoryFooterActions({
  chatUrl,
  onClose,
  onOpenChat,
  onOpenChatInNewTab,
}) {
  const [anchorEl, setAnchorEl] = useState(null);
  const menuOpen = Boolean(anchorEl);
  const canOpenChat = Boolean(chatUrl);

  const closeMenu = () => {
    setAnchorEl(null);
  };

  const openChatHere = () => {
    closeMenu();
    if (!chatUrl) return;

    if (onOpenChat) {
      onOpenChat(chatUrl);
    }
  };

  const openChatInNewTab = () => {
    closeMenu();
    if (!chatUrl) return;

    if (onOpenChatInNewTab) {
      onOpenChatInNewTab(chatUrl);
    }
  };

  return (
    <Box
      sx={{
        alignItems: "center",
        borderTop: "1px solid",
        borderColor: "border.default",
        display: "flex",
        flexShrink: 0,
        gap: 1,
        justifyContent: "flex-end",
        minHeight: 58,
        px: { xs: 2, md: 3 },
        py: 1,
      }}
    >
      <Button
        onClick={onClose}
        size="small"
        sx={{
          borderRadius: 999,
          minHeight: 36,
          minWidth: 106,
          px: 2.5,
          textTransform: "none",
        }}
        variant="outlined"
      >
        Fechar
      </Button>
      <Button
        disabled={!canOpenChat}
        endIcon={<KeyboardArrowDownRoundedIcon sx={{ fontSize: 18 }} />}
        onClick={(event) => setAnchorEl(event.currentTarget)}
        size="small"
        startIcon={<OpenInNewRoundedIcon sx={{ fontSize: 18 }} />}
        sx={{
          borderRadius: 999,
          minHeight: 36,
          px: 2.25,
          textTransform: "none",
        }}
        variant="outlined"
      >
        Abrir atendimento
      </Button>
      <Menu
        anchorEl={anchorEl}
        anchorOrigin={{ horizontal: "right", vertical: "top" }}
        onClose={closeMenu}
        open={menuOpen}
        transformOrigin={{ horizontal: "right", vertical: "bottom" }}
      >
        <MenuItem onClick={openChatHere}>Abrir aqui</MenuItem>
        <MenuItem onClick={openChatInNewTab}>Abrir em nova guia</MenuItem>
      </Menu>
    </Box>
  );
}

export function ConversationHistoryPanel({
  availabilityUpdating = false,
  chatUrl,
  contact,
  initialCategoryFilter = "campaigns",
  loading,
  messageDetailLoadingByKey = {},
  messageDetailsByKey = {},
  messages = [],
  nextCursor = null,
  onChangeAvailability,
  onClose,
  onOpenChat,
  onOpenChatInNewTab,
  onLoadMore,
  onLoadMoreTimeline,
  onLoadMediaAccess,
  onPrefetchPreviewDetails,
  onSelectMessage,
  open = true,
  selectedMessageDetail,
  selectedMessageDetailLoading,
  selectedMessage,
  showFooterActions = false,
  showHeaderClose = true,
  showHistoryNavigation = true,
  timelineLoading = false,
  timelineMessages = [],
  timelineNextCursor = null,
}) {
  const [historyCategoryFilter, setHistoryCategoryFilter] = useState(initialCategoryFilter);
  const [historyChannelFilter, setHistoryChannelFilter] = useState("all");
  const [historySearchOpen, setHistorySearchOpen] = useState(false);
  const [historySearchTerm, setHistorySearchTerm] = useState("");
  const [previousVisibleCount, setPreviousVisibleCount] = useState(0);
  const [expandPreviousAfterLoad, setExpandPreviousAfterLoad] = useState(false);
  const [previousDetailsLoading, setPreviousDetailsLoading] = useState(false);
  const isGeneralView = historyCategoryFilter === "general";
  const activeMessages = isGeneralView ? timelineMessages : messages;
  const activeLoading = historyCategoryFilter === "general" ? timelineLoading : loading;
  const activeNextCursor = historyCategoryFilter === "general" ? timelineNextCursor : nextCursor;
  const activeLoadMore = historyCategoryFilter === "general" ? onLoadMoreTimeline : onLoadMore;
  const hasMessages = activeMessages.length > 0;
  const visibleMessages = useMemo(() => {
    if (isGeneralView) {
      const filteredDays = activeMessages
        .map((dayGroup) => filterDayGroupItems(dayGroup, historyChannelFilter, historySearchTerm))
        .filter((dayGroup) => {
          if (dayGroup.items.length) return true;
          return (
            historyChannelFilter === "all" &&
            dayMatchesSearch(dayGroup, historySearchTerm)
          );
        });

      return filteredDays.sort((firstDay, secondDay) => {
        const firstDate = parseDayDate(firstDay);
        const secondDate = parseDayDate(secondDay);
        return secondDate - firstDate;
      });
    }

    const filteredMessages = activeMessages.filter((message) => {
      const matchesChannel =
        historyChannelFilter === "all" || message.channel_type === historyChannelFilter;

      return (
        matchesChannel &&
        messageMatchesSearch(message, historySearchTerm)
      );
    });

    return filteredMessages.sort((firstMessage, secondMessage) => {
      const firstDate = parseMessageDate(firstMessage);
      const secondDate = parseMessageDate(secondMessage);
      return secondDate - firstDate;
    });
  }, [
    historyChannelFilter,
    historySearchTerm,
    activeMessages,
    isGeneralView,
  ]);
  const selectedVisibleMessage = useMemo(() => {
    if (!selectedMessage) return null;

    const selectedKey = isGeneralView
      ? getDayKey(selectedMessage, 0)
      : getMessageKey(selectedMessage, 0);

    return visibleMessages.find((message, index) => (
      isGeneralView
        ? getDayKey(message, index) === selectedKey
        : getMessageKey(message, index) === selectedKey
    )) || selectedMessage;
  }, [isGeneralView, selectedMessage, visibleMessages]);
  const selectedVisibleKey = selectedVisibleMessage
    ? isGeneralView
      ? getDayKey(selectedVisibleMessage, 0)
      : getMessageKey(selectedVisibleMessage, 0)
    : null;
  const selectedVisibleIndex = selectedVisibleKey
    ? visibleMessages.findIndex((message, index) => (
        isGeneralView
          ? getDayKey(message, index) === selectedVisibleKey
          : getMessageKey(message, index) === selectedVisibleKey
      ))
    : -1;
  const previousCandidateIndex = selectedVisibleIndex >= 0
    ? selectedVisibleIndex + previousVisibleCount + 1
    : -1;
  const previousLoadedMessage = previousCandidateIndex >= 0
    ? visibleMessages[previousCandidateIndex] || null
    : null;
  const accumulatedVisibleMessages = useMemo(() => {
    if (selectedVisibleIndex < 0) {
      return selectedVisibleMessage ? [selectedVisibleMessage] : [];
    }

    const visibleEndIndex = Math.min(
      visibleMessages.length,
      selectedVisibleIndex + previousVisibleCount + 1,
    );

    return visibleMessages.slice(selectedVisibleIndex, visibleEndIndex).slice().reverse();
  }, [previousVisibleCount, selectedVisibleIndex, selectedVisibleMessage, visibleMessages]);
  const hasVisibleMessages = visibleMessages.length > 0;
  const selectionPending = Boolean(hasVisibleMessages && !selectedVisibleMessage);
  const hasPendingPreviewDetails = useMemo(() => {
    if (!hasVisibleMessages || selectionPending) return false;

    return hasPendingCampaignDetailsForEntries(
      accumulatedVisibleMessages,
      messageDetailsByKey,
      messageDetailLoadingByKey,
    );
  }, [
    accumulatedVisibleMessages,
    hasVisibleMessages,
    messageDetailLoadingByKey,
    messageDetailsByKey,
    selectionPending,
  ]);
  const chatLoading = Boolean(
    (activeLoading && !hasMessages) ||
      selectionPending ||
      hasPendingPreviewDetails,
  );
  const chatRevealKey = [
    historyCategoryFilter,
    selectedVisibleKey || "empty",
  ].join("|");
  const previousMessagesLoading = Boolean(
    previousDetailsLoading ||
      (expandPreviousAfterLoad && (activeLoading || hasPendingPreviewDetails)),
  );
  const hasPreviousMessages = Boolean(previousLoadedMessage || (activeNextCursor && activeLoadMore));
  const handleLoadPreviousMessages = () => {
    if (previousLoadedMessage) {
      const nextEntries = [previousLoadedMessage];
      const shouldWaitForDetails = hasPendingCampaignDetailsForEntries(
        nextEntries,
        messageDetailsByKey,
        messageDetailLoadingByKey,
      );

      setPreviousDetailsLoading(shouldWaitForDetails);
      setPreviousVisibleCount((currentCount) => currentCount + 1);
      if (shouldWaitForDetails && onPrefetchPreviewDetails) {
        onPrefetchPreviewDetails(nextEntries);
      }
      return;
    }

    if (activeNextCursor && activeLoadMore) {
      setExpandPreviousAfterLoad(true);
      activeLoadMore();
    }
  };
  const contactHeader = getContactHeader(contact);
  const isBlocked = contact?.availability_status === "blocked" || contact?.is_suppressed;
  const availabilityAction = isBlocked ? "available" : "blocked";
  const availabilityLabel = isBlocked
    ? "Liberar envios para este contato"
    : "Bloquear envios para este contato";
  const AvailabilityIcon = isBlocked ? CheckCircleOutlineRoundedIcon : BlockRoundedIcon;
  const showAvailabilityAction = Boolean(contact);

  useEffect(() => {
    if (!hasPendingPreviewDetails || !onPrefetchPreviewDetails) return;

    onPrefetchPreviewDetails(accumulatedVisibleMessages);
  }, [
    accumulatedVisibleMessages,
    hasPendingPreviewDetails,
    onPrefetchPreviewDetails,
  ]);

  useEffect(() => {
    setPreviousVisibleCount(0);
    setExpandPreviousAfterLoad(false);
    setPreviousDetailsLoading(false);
  }, [
    historyCategoryFilter,
    historyChannelFilter,
    historySearchTerm,
    open,
    selectedVisibleKey,
  ]);

  useEffect(() => {
    if (!expandPreviousAfterLoad || activeLoading || hasPendingPreviewDetails) return;

    if (previousLoadedMessage) {
      const nextEntries = [previousLoadedMessage];
      const shouldWaitForDetails = hasPendingCampaignDetailsForEntries(
        nextEntries,
        messageDetailsByKey,
        messageDetailLoadingByKey,
      );

      if (shouldWaitForDetails && onPrefetchPreviewDetails) {
        onPrefetchPreviewDetails(nextEntries);
      }
      setPreviousDetailsLoading(shouldWaitForDetails);
      setPreviousVisibleCount((currentCount) => currentCount + 1);
      setExpandPreviousAfterLoad(false);
      return;
    }

    if (!activeNextCursor) {
      setExpandPreviousAfterLoad(false);
    }
  }, [
    activeLoading,
    activeNextCursor,
    expandPreviousAfterLoad,
    hasPendingPreviewDetails,
    messageDetailLoadingByKey,
    messageDetailsByKey,
    onPrefetchPreviewDetails,
    previousLoadedMessage,
  ]);

  useEffect(() => {
    if (!previousDetailsLoading || hasPendingPreviewDetails) return;

    setPreviousDetailsLoading(false);
  }, [hasPendingPreviewDetails, previousDetailsLoading]);

  useEffect(() => {
    if (!open) return;

    if (!hasVisibleMessages) {
      if (selectedMessage) {
        onSelectMessage(null);
      }
      return;
    }

    const selectedKey = selectedMessage
      ? isGeneralView
        ? getDayKey(selectedMessage, 0)
        : getMessageKey(selectedMessage, 0)
      : null;
    const selectedVisible = selectedKey
      ? visibleMessages.some((message, index) =>
          isGeneralView
            ? getDayKey(message, index) === selectedKey
            : getMessageKey(message, index) === selectedKey,
        )
      : false;

    if (!selectedVisible) {
      onSelectMessage(visibleMessages[0]);
    }
  }, [hasVisibleMessages, isGeneralView, onSelectMessage, open, selectedMessage, visibleMessages]);

  return (
    <Box sx={{ display: "flex", flex: 1, flexDirection: "column", minHeight: 0, overflow: "hidden", p: 0 }}>
        <Box
          sx={{
            alignItems: "center",
            borderBottom: "1px solid",
            borderColor: "border.default",
            display: "flex",
            gap: 1.5,
            minHeight: 64,
            px: { xs: 2, md: 3 },
          }}
        >
          <Avatar
            sx={(currentTheme) => ({
              backgroundColor: currentTheme.palette.custom.blue[50],
              color: currentTheme.palette.custom.blue[700],
              fontSize: 13,
              fontWeight: 900,
              height: 38,
              width: 38,
            })}
          >
            {initialsFromContact(contact)}
          </Avatar>

          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography
              sx={{
                color: "text.primary",
                fontSize: 15,
                fontWeight: 900,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
            }}
          >
              {contactHeader.title}
            </Typography>
            <Typography
              sx={{
                color: "text.secondary",
                fontSize: 12,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {contactHeader.subtitle}
            </Typography>
          </Box>

          {showAvailabilityAction ? (
            <Tooltip title={availabilityLabel}>
              <span>
                <IconButton
                  aria-label={availabilityLabel}
                  disabled={availabilityUpdating || loading || !onChangeAvailability}
                  onClick={() => onChangeAvailability?.(availabilityAction)}
                  size="small"
                  sx={(currentTheme) => ({
                    borderRadius: "8px",
                    color: isBlocked
                      ? currentTheme.palette.success.main
                      : currentTheme.palette.error.main,
                    height: 30,
                    width: 30,
                    "&:hover": {
                      backgroundColor: isBlocked
                        ? alpha(currentTheme.palette.success.main, 0.08)
                        : alpha(currentTheme.palette.error.main, 0.08),
                    },
                  })}
                >
                  {availabilityUpdating ? (
                    <CircularProgress color="inherit" size={15} />
                  ) : (
                    <AvailabilityIcon sx={{ fontSize: 18 }} />
                  )}
                </IconButton>
              </span>
            </Tooltip>
          ) : null}

          {showHeaderClose ? (
            <IconButton aria-label="Fechar" onClick={onClose} size="small">
              <CloseRoundedIcon fontSize="small" />
            </IconButton>
          ) : null}
        </Box>

        <Box sx={{ display: "flex", flex: 1, minHeight: 0, overflow: "hidden" }}>
          {showHistoryNavigation ? (
            <Box
              sx={(theme) => ({
                borderRight: { xs: "0", md: `1px solid ${theme.palette.border.default}` },
                display: { xs: selectedVisibleMessage ? "none" : "flex", md: "flex" },
                flexDirection: "column",
                minHeight: 0,
                width: { xs: "100%", md: 360 },
              })}
            >
              <MessageHistoryFilters
                channelFilter={historyChannelFilter}
                categoryFilter={historyCategoryFilter}
                onChannelFilterChange={setHistoryChannelFilter}
                onCategoryFilterChange={setHistoryCategoryFilter}
                onSearchTermChange={setHistorySearchTerm}
                searchOpen={historySearchOpen}
                searchTerm={historySearchTerm}
                setSearchOpen={setHistorySearchOpen}
              />

              <Box
                sx={{
                  flex: 1,
                  minHeight: 0,
                  msOverflowStyle: "none",
                  overflowX: "hidden",
                  overflowY: "auto",
                  scrollbarWidth: "none",
                  width: "100%",
                  "&::-webkit-scrollbar": {
                    display: "none",
                  },
                }}
              >
                {activeLoading && !hasMessages ? (
                  <Box sx={{ alignItems: "center", display: "flex", justifyContent: "center", minHeight: 220 }}>
                    <CircularProgress size={28} />
                  </Box>
                ) : null}

                {!activeLoading && !hasMessages ? (
                  <Box sx={{ px: 3, py: 8, textAlign: "center" }}>
                    <Typography sx={{ color: "text.secondary", fontSize: 13, fontWeight: 500 }}>
                      Nenhum item encontrado.
                    </Typography>
                  </Box>
                ) : null}

                {!activeLoading && hasMessages && !hasVisibleMessages ? (
                  <Box sx={{ px: 3, py: 8, textAlign: "center" }}>
                    <Typography sx={{ color: "text.secondary", fontSize: 13, fontWeight: 500 }}>
                      Nenhum item encontrado.
                    </Typography>
                  </Box>
                ) : null}

                {visibleMessages.map((message, index) => {
                  if (isGeneralView) {
                    const key = getDayKey(message, index);

                    return (
                      <TimelineDayListItem
                        active={selectedVisibleKey === key}
                        dayGroup={message}
                        key={key}
                        onClick={() => onSelectMessage(message)}
                      />
                    );
                  }

                  const key = getMessageKey(message, index);
                  return (
                    <MessageListItem
                      active={selectedVisibleKey === key}
                      key={key}
                      message={message}
                      onClick={() => onSelectMessage(message)}
                    />
                  );
                })}
              </Box>

              {activeNextCursor ? (
                <Box sx={{ borderTop: "1px solid", borderColor: "border.default", p: 1.5 }}>
                  <Button
                    disabled={activeLoading}
                    fullWidth
                    onClick={activeLoadMore}
                    size="small"
                    sx={{ borderRadius: 1, textTransform: "none" }}
                    variant="outlined"
                  >
                    {activeLoading ? "Carregando..." : "Carregar mais"}
                  </Button>
                </Box>
              ) : null}
            </Box>
          ) : null}

          <Box
            sx={{
              display: {
                xs: showHistoryNavigation && !selectedVisibleMessage ? "none" : "flex",
                md: "flex",
              },
              flex: 1,
              minHeight: 0,
              minWidth: 0,
              overflow: "hidden",
            }}
          >
            {chatLoading ? (
              <MessageHistoryChatLoading />
            ) : (
              <MessageHistoryContentReveal revealKey={chatRevealKey}>
                {isGeneralView ? (
                  <TimelineDayPreview
                    contact={contact}
                    contactPointId={contact?.id}
                    dayGroup={selectedVisibleMessage}
                    dayGroups={accumulatedVisibleMessages}
                    hasPreviousMessages={hasPreviousMessages}
                    loadingPreviousMessages={previousMessagesLoading}
                    messageDetailLoadingByKey={messageDetailLoadingByKey}
                    messageDetailsByKey={messageDetailsByKey}
                    onLoadMediaAccess={onLoadMediaAccess}
                    onLoadPreviousMessages={handleLoadPreviousMessages}
                  />
                ) : (
                  <MessagePreview
                    contact={contact}
                    contactPointId={contact?.id}
                    detail={selectedMessageDetail}
                    detailLoading={selectedMessageDetailLoading}
                    hasPreviousMessages={hasPreviousMessages}
                    loadingPreviousMessages={previousMessagesLoading}
                    message={selectedVisibleMessage}
                    messageDetailLoadingByKey={messageDetailLoadingByKey}
                    messageDetailsByKey={messageDetailsByKey}
                    messages={accumulatedVisibleMessages}
                    onLoadMediaAccess={onLoadMediaAccess}
                    onLoadPreviousMessages={handleLoadPreviousMessages}
                  />
                )}
              </MessageHistoryContentReveal>
            )}
          </Box>
        </Box>
        {showFooterActions ? (
          <MessageHistoryFooterActions
            chatUrl={chatUrl}
            onClose={onClose}
            onOpenChat={onOpenChat}
            onOpenChatInNewTab={onOpenChatInNewTab}
          />
        ) : null}
      </Box>
  );
}

export default function ConversationHistoryDialog(props) {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down("md"));

  return (
    <Dialog
      fullScreen={fullScreen}
      fullWidth
      maxWidth={false}
      onClose={props.onClose}
      open={props.open}
      PaperProps={{
        sx: {
          borderRadius: { xs: 0, md: "8px" },
          height: { xs: "100%", md: "82vh" },
          maxHeight: { xs: "100%", md: 760 },
          maxWidth: { xs: "100%", md: "min(1180px, calc(100vw - 48px))", lg: "min(1240px, calc(100vw - 64px))" },
          overflow: "hidden",
          width: { xs: "100%", md: "min(1180px, calc(100vw - 48px))", lg: "min(1240px, calc(100vw - 64px))" },
        },
      }}
    >
      <ConversationHistoryPanel {...props} showFooterActions />
    </Dialog>
  );
}
